import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { civilYearMonth, localIsoDate } from '../../shared/civil-date';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { PropertyService } from '../../services/property.service';
import { OrganizationService } from '../../services/organization.service';
import { DEFAULT_EXPENSE_CATEGORIES } from '../../constants/expense-categories';

// Chart is loaded globally via script tag in index.html
declare var Chart: any;

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit, AfterViewInit {
  properties: any[] = [];
  selectedProperty: any = null;
  organization: any;
  startDate: string;
  endDate: string;
  cumulative = true;
  selectedDateFilter: string = 'currentYear'; // 'custom', 'currentYear', 'previousYear'
  expenseCategories: string[] = [...DEFAULT_EXPENSE_CATEGORIES];
  showCategories = false;
  tableData: any[] = [];
  tableDataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatPaginator) paginator: MatPaginator;

  private incomeChart: any;
  private expensesChart: any;
  private profitChart: any;
  private dataCache: {
    [key: string]: { invoices: any[]; expenses: any[] };
  } = {};
  private colorPalette: string[] = [
    '#3366CC',
    '#DC3912',
    '#FF9900',
    '#109618',
    '#990099',
    '#0099C6',
    '#DD4477',
    '#66AA00',
    '#B82E2E',
    '#316395'
  ];
  private viewReady = false;
  private totalPlugin = {
    id: 'stackTotals',
    afterDatasetsDraw: (chart: any) => {
      const {
        ctx,
        data: { datasets },
        scales: { x, y }
      } = chart;
      chart.data.labels.forEach((_: any, i: number) => {
        let total = 0;
        datasets.forEach((ds: any) => {
          const val = Number(ds.data[i]) || 0;
          total += val;
        });
        const xPos = x.getPixelForValue(i);
        const yPos = y.getPixelForValue(total);
        ctx.save();
        ctx.fillStyle = '#000';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'center';
        const offset = total >= 0 ? -5 : 15;
        ctx.fillText(total.toFixed(0), xPos, yPos + offset);
        ctx.restore();
      });
    }
  };

  constructor(
    private propertyService: PropertyService,
    private organizationService: OrganizationService
  ) {}

  async ngOnInit() {
    // Set default to current year
    this.setDateFilter('currentYear');

    this.organizationService.getExpenseCategories().then(categories => {
      if (Array.isArray(categories) && categories.length > 0) {
        this.expenseCategories = categories;
      }
    }).catch(() => {
      // Keep the default list on failure
    });

    // Reports must include archived properties: their historical invoices and
    // expenses still count for tax and year-over-year figures.
    const [props, org] = await Promise.all([
      this.propertyService.getProperties(true),
      this.organizationService.getOrganization()
    ]);
    this.properties = props || [];
    this.organization = org;

    await Promise.all([
      ...this.properties.map(p =>
        Promise.all([
          this.propertyService.getInvoices(p),
          this.organizationService.getAllExpenses(undefined, undefined, p.id)
        ]).then(([invoicesResponse, expensesResponse]: [any, any]) => {
          // Handle paginated or non-paginated response for invoices
          let invoices;
          if (invoicesResponse && typeof invoicesResponse === 'object' && invoicesResponse.data) {
            invoices = invoicesResponse.data;
          } else if (Array.isArray(invoicesResponse)) {
            invoices = invoicesResponse;
          } else {
            invoices = [];
          }
          
          // Handle paginated or non-paginated response for expenses
          const expenses = (expensesResponse && (expensesResponse as any).data) 
            ? (expensesResponse as any).data 
            : (Array.isArray(expensesResponse) ? expensesResponse : []);
          
          this.dataCache[p.id] = { invoices, expenses };
        })
      ),
      Promise.resolve().then(() => {
        const orgInvoices = (org && org.invoices) || [];
        const orgExpenses = (org && org.expenses) || [];
        this.dataCache['organization'] = { invoices: orgInvoices, expenses: orgExpenses };
      })
    ]);

    if (this.viewReady) {
      this.updateCharts();
    }
  }

  ngAfterViewInit() {
    this.viewReady = true;
    if (this.paginator && this.tableDataSource) {
      this.tableDataSource.paginator = this.paginator;
    }
    this.updateCharts();
  }

  toggleCategories() {
    this.showCategories = !this.showCategories;
  }

  onDateFilterChange(filterType: string) {
    this.selectedDateFilter = filterType;
    this.setDateFilter(filterType);
    this.updateCharts();
  }

  setDateFilter(filterType: string) {
    const today = new Date();
    
    if (filterType === 'currentYear') {
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      this.startDate = localIsoDate(startOfYear);
      this.endDate = localIsoDate(today);
    } else if (filterType === 'previousYear') {
      const startOfPreviousYear = new Date(today.getFullYear() - 1, 0, 1);
      const endOfPreviousYear = new Date(today.getFullYear() - 1, 11, 31);
      this.startDate = localIsoDate(startOfPreviousYear);
      this.endDate = localIsoDate(endOfPreviousYear);
    }
    // For 'custom', dates are set manually by user
  }

  onCustomDateChange() {
    if (this.startDate && this.endDate) {
      this.selectedDateFilter = 'custom';
      this.updateCharts();
    }
  }

  getDisplayedColumns(): string[] {
    const columns = ['property', 'income'];
    if (this.showCategories) {
      // Add category columns with prefix to match matColumnDef
      this.expenseCategories.forEach(cat => {
        columns.push(`category-${cat}`);
      });
    }
    columns.push('expensesTotal', 'profit');
    return columns;
  }

  updateCharts() {
    const { labels, months } = this.buildLabels();
    const keys = this.selectedProperty ? [this.selectedProperty.id] : Object.keys(this.dataCache);

    const incomeDatasets: any[] = [];
    const expensesDatasets: any[] = [];
    const monthlyIncomeTotals = Array(labels.length).fill(0);
    const monthlyExpenseTotals = Array(labels.length).fill(0);
    this.tableData = [];

    keys.forEach((key, index) => {
      const series = this.computeSeriesForKey(key, months);
      let income = [...series.income];
      let expenses = [...series.expenses];

      income.forEach((val, i) => (monthlyIncomeTotals[i] += val));
      expenses.forEach((val, i) => (monthlyExpenseTotals[i] += val));

      if (this.cumulative) {
        income = this.accumulate(income);
        expenses = this.accumulate(expenses);
      }

      const color = this.colorPalette[index % this.colorPalette.length];
      const label = this.getLabelForKey(key);

      incomeDatasets.push({
        label,
        data: income,
        backgroundColor: color,
        borderColor: color
      });

      expensesDatasets.push({
        label,
        data: expenses,
        backgroundColor: color,
        borderColor: color
      });

      const incomeTotal = series.income.reduce((a, b) => a + b, 0);
      const expensesTotal = series.expenses.reduce((a, b) => a + b, 0);
      const profit = incomeTotal - expensesTotal;

      this.tableData.push({
        label,
        income: incomeTotal,
        expensesByCategory: series.expenseBreakdown,
        expensesTotal,
        profit
      });
    });

    // Update table data source
    this.tableDataSource.data = this.tableData;
    if (this.paginator) {
      this.tableDataSource.paginator = this.paginator;
    }

    let profitTotals = monthlyIncomeTotals.map((v, i) => v - monthlyExpenseTotals[i]);
    if (this.cumulative) {
      profitTotals = this.accumulate(profitTotals);
    }

    this.incomeChart = this.buildChart(this.incomeChart, 'incomeCanvas', labels, incomeDatasets, true);
    this.expensesChart = this.buildChart(this.expensesChart, 'expensesCanvas', labels, expensesDatasets, true);
    this.profitChart = this.buildChart(this.profitChart, 'profitCanvas', labels, [
      {
        label: 'Profit',
        data: profitTotals,
        backgroundColor: '#66AA00',
        borderColor: '#66AA00'
      }
    ], false);

    // Update table data source
    this.tableDataSource.data = this.tableData;
    if (this.paginator) {
      this.tableDataSource.paginator = this.paginator;
    }
  }

  private buildChart(chartRef: any, canvasId: string, labels: string[], datasets: any[], stacked = false) {
    const ctx = (document.getElementById(canvasId) as HTMLCanvasElement).getContext('2d');
    if (chartRef) {
      chartRef.destroy();
    }
    return new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: { stacked },
          y: { stacked }
        }
      },
      plugins: [this.totalPlugin]
    });
  }

  private getLabelForKey(key: string): string {
    if (key === 'organization') {
      return 'Organization';
    }
    const prop = this.properties.find(p => p.id === key);
    if (!prop) {
      return key;
    }
    // Units share a street address (e.g. three "2191 E Main St" suites): include line 2.
    const line2 = (prop.addressLine2 || '').trim();
    return line2 ? `${prop.addressLine1} ${line2}` : prop.addressLine1;
  }

  private buildLabels() {
    let start: Date;
    let end: Date;
    if (this.startDate) {
      start = this.parseLocalDate(this.startDate);
    } else {
      const now = new Date();
      start = new Date(now.getFullYear(), 0, 1);
    }
    if (this.endDate) {
      end = this.parseLocalDate(this.endDate);
    } else {
      end = new Date();
    }

    const startYM = this.getEasternYearMonth(start);
    const endYM = this.getEasternYearMonth(end);

    const months: { year: number; month: number }[] = [];
    let y = startYM.year;
    let m = startYM.month;
    while (y < endYM.year || (y === endYM.year && m <= endYM.month)) {
      months.push({ year: y, month: m });
      m++;
      if (m > 11) {
        m = 0;
        y++;
      }
    }
    const labels = months.map(({ year, month }) =>
      new Date(Date.UTC(year, month, 1, 12)).toLocaleString('default', {
        month: 'short',
        timeZone: 'America/New_York'
      })
    );
    return { labels, months };
  }

  private parseLocalDate(value: string): Date {
    const [y, m, d] = value.split('-').map(n => parseInt(n, 10));
    return new Date(y, m - 1, d);
  }

  private computeSeriesForKey(key: string, months: { year: number; month: number }[]) {
    const cached = this.dataCache[key] || { invoices: [], expenses: [] };
    const income = Array(months.length).fill(0);
    const expenses = Array(months.length).fill(0);
    const expenseBreakdown: { [cat: string]: number } = {};
    this.expenseCategories.forEach(cat => (expenseBreakdown[cat] = 0));

    // Ensure invoices is an array
    const invoices = Array.isArray(cached.invoices) ? cached.invoices : [];
    
    invoices.forEach(inv => {
      // Statement invoices (managed properties): the gross income on the statement
      // is the invoice amount for the statement month. Their payments ("Collected
      // by manager", legacy "Statement Adjustment") are not counted again, and owner
      // distributions are recorded without an invoice, so they never count as income.
      if (inv.statementId) {
        const ym = civilYearMonth(inv.date || inv.dueDate);
        const idx = ym ? this.getMonthIndex(ym, months) : -1;
        if (idx >= 0) {
          income[idx] += Number(inv.amount) || 0;
        }
        return;
      }

      const payments = inv.payments || [];
      
      payments.forEach(pay => {
        if (!pay.paymentDate) {
          return; // Skip payments without dates
        }
        if (pay.statementId) {
          // Statement-linked payments ("Collected by manager", legacy "Statement
          // Adjustment") settle statement invoices whose income is counted above.
          return;
        }
        
        // paymentDate is returned as a date string (no timezone) from the backend.
        // Using `new Date()` on such a string interprets it as UTC which can shift
        // midnight payments into the previous month when converted to Eastern time.
        // Parse the date in the local timezone first to preserve the intended day.
        const paymentDate = this.parseLocalDate(pay.paymentDate);
        const ym = this.getEasternYearMonth(paymentDate);
        const idx = this.getMonthIndex(ym, months);
        
        if (idx >= 0) {
          income[idx] += Number(pay.amount);
        }
      });
    });

    // Ensure expenses is an array
    const expensesList = Array.isArray(cached.expenses) ? cached.expenses : [];
    expensesList.forEach(exp => {
      // Statement expenses are dated at UTC midnight on the 1st; read the calendar
      // month instead of shifting them into the previous month in Eastern time.
      const ym = civilYearMonth(exp.date);
      const idx = ym ? this.getMonthIndex(ym, months) : -1;
      if (idx >= 0) {
        const amt = Number(exp.amount);
        expenses[idx] += amt;
        const cat = this.formatCategory(exp.category);
        expenseBreakdown[cat] = (expenseBreakdown[cat] || 0) + amt;
      }
    });

    return { income, expenses, expenseBreakdown };
  }

  private getMonthIndex(
    date: { year: number; month: number },
    months: { year: number; month: number }[]
  ): number {
    for (let i = 0; i < months.length; i++) {
      if (months[i].year === date.year && months[i].month === date.month) {
        return i;
      }
    }
    return -1;
  }

  private getEasternYearMonth(date: Date): { year: number; month: number } {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'numeric'
    }).formatToParts(date);
    const yearPart = parts.find(p => p.type === 'year');
    const monthPart = parts.find(p => p.type === 'month');
    const year = yearPart ? Number(yearPart.value) : NaN;
    const month = monthPart ? Number(monthPart.value) - 1 : NaN;
    return { year, month };
  }

  private formatCategory(cat: string): string {
    switch ((cat || '').toUpperCase()) {
      case 'ADVERTISING':
        return 'Advertising';
      case 'CAR_AND_TRUCK_EXPENSES':
        return 'Car and Truck Expenses';
      case 'COMMISSIONS_AND_FEES':
        return 'Commissions and Fees';
      case 'CONTRACT_LABOR':
        return 'Contract Labor';
      case 'DEPRECIATION':
        return 'Depreciation';
      case 'EMPLOYEE_WAGES':
        return 'Employee Wages';
      case 'INSURANCE_OTHER_THAN_HEALTH':
        return 'Insurance (Other than Health)';
      case 'INTEREST_MORTGAGE_AND_OTHER':
        return 'Interest (Mortgage and Other)';
      case 'LEGAL_AND_PROFESSIONAL_SERVICES':
        return 'Legal and Professional Services';
      case 'OFFICE_EXPENSES':
        return 'Office Expenses';
      case 'RENT_OR_LEASE':
        return 'Rent or Lease';
      case 'REPAIRS_AND_MAINTENANCE':
        return 'Repairs and Maintenance';
      case 'SUPPLIES':
        return 'Supplies';
      case 'TAXES_AND_LICENSES':
        return 'Taxes and Licenses';
      case 'TRAVEL':
        return 'Travel';
      case 'MEALS':
        return 'Meals';
      case 'UTILITIES':
        return 'Utilities';
      case 'OTHER_EXPENSES':
      default:
        return 'Other Expenses';
    }
  }

  private accumulate(data: number[]): number[] {
    const result: number[] = [];
    data.reduce((prev, curr, idx) => {
      const sum = prev + curr;
      result[idx] = sum;
      return sum;
    }, 0);
    return result;
  }
}
